/**
 * Route copy for `src/routes/marketplace` (keys: `routes.marketplace.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  browsePage: {
    topRated: 'Top rated',
    mostReviews: 'Most reviews',
    recent: 'Recent',
    priceAscending: 'Price ascending',
    available: 'Available',
    busy: 'Busy',
    unavailable: 'Unavailable',
    expertise: 'Expertise',
    verified: 'Verified',
    viewTheProfileOfDisplayName: 'View the profile of {{display_name}}',
    noReviewsYet: 'No reviews yet',
    topRated2: 'Top rated',
    averageRatingAggregateRatingOutOf: 'Average rating {{aggregate_rating}} out of 5',
    reviews: 'Reviews',
    remote: 'Remote',
    couldNotLoadTheProviders: 'Could not load the providers.',
    providers: 'Providers',
    services: 'Services',
    searchForAProvider: 'Search for a provider…',
    search: 'Search',
    category: 'Category',
    allCategories: 'All categories',
    expertiseEGSeo: 'Expertise (e.g. seo)',
    expertise2: 'Expertise',
    minimumRating: 'Minimum rating',
    allRatings: 'All ratings',
    n4AndUp: '4★ and up',
    n3AndUp: '3★ and up',
    n2AndUp: '2★ and up',
    maxPrice: 'Max price',
    maximumPrice: 'Maximum price',
    sort: 'Sort',
    searching: 'Searching…',
    totalProviderS: '{{total}} provider(s)',
    reset: 'Reset',
    averageRating: 'Average rating',
    verified2: 'Verified',
    noProviderMatchesTheseCriteria: 'No provider matches these criteria.',
    viewTheServiceTitle: 'View the service {{title}}',
    deliveryDeliveryTime: 'Delivery: {{delivery_time}}',
    leadTimeOnRequest: 'Lead time on request',
    inactive: 'Inactive',
    thisServiceIsNoLongerAvailable: 'This service is no longer available.',
    refreshing: 'Refreshing…',
    noDescriptionProvided: 'No description provided.',
    publishedOnV: 'Published on {{v}}',
    viewTheProvider: 'View the provider',
    couldNotLoadTheServices: 'Could not load the services.',
    searchForAService: 'Search for a service…',
    serviceSearch: 'Service search',
    totalServiceS: '{{total}} service(s)',
    noServiceMatchesTheseCriteria: 'No service matches these criteria.',
  },
  components: {
    countReviews: ' ({{count}} reviews)',
    outOf5: 'out of 5',
    countReviews2: ', {{count}} reviews',
    nStarS: '{{n}} star(s)',
    loading: 'Loading…',
  },
  marketplaceLayout: {
    catalog: 'Catalogue',
    moderation: 'Moderation',
    explore: 'Explore',
    providerSpace: 'Provider space',
    myBookings: 'My bookings',
    myReviews: 'My reviews',
    servicesMarketplace: 'Services marketplace',
    discoverProvidersBookTheirServicesAnd: 'Discover providers, book their services and review them.',
    marketplaceNavigation: 'Marketplace navigation',
  },
  myBookingsPage: {
    forListing: 'Listing: {{title}} — {{provider}}',
    reviewTitle: 'Review “{{title}}”',
    overallRating: 'Overall rating',
    reviewComment: 'Comment (optional)',
    publishReview: 'Publish the review',
    couldNotReview: 'Could not submit the review.',
    reviewThanks: 'Thank you for your review!',
    thisFeatureIsNotIncludedIn: 'This feature is not included in your current plan.',
    viewThePlans: 'View the plans',
    allStatuses: 'All statuses',
    requested: 'Requested',
    accepted: 'Accepted',
    inProgress: 'In progress',
    done: 'Done',
    cancelled: 'Cancelled',
    declined: 'Declined',
    couldNotLoadYourBookings: 'Could not load your bookings.',
    requestCancelled: 'Request cancelled.',
    couldNotCancel: 'Could not cancel.',
    paymentsAreNotEnabledYet: 'Payments are not enabled yet.',
    paymentNotPossible: 'Payment not possible.',
    filterByStatus: 'Filter by status',
    lengthBookingS: '{{length}} booking(s)',
    youHaveNoBookingsYet: 'You have no bookings yet.',
    exploreTheProviders: 'Explore the providers',
    quote: 'Quote',
    reservation: 'Reservation',
    createdOnV: 'Created on {{v}}',
    redirecting: 'Redirecting…',
    payConfirm: 'Pay & confirm',
    cancelling: 'Cancelling…',
    cancel: 'Cancel',
    review: 'Review',
    viewTheProvider: 'View the provider',
  },
  myReviewsPage: {
    couldNotLoadYourReviews: 'Could not load your reviews.',
    youHaveNotPublishedAReview: 'You have not published a review yet.',
    exploreTheProviders: 'Explore the providers',
    viewTheProvider: 'View the provider',
  },
  providerDashboardPage: {
    published: 'Published',
    draft: 'Draft',
    removedByModeration: 'Removed by moderation',
    publish: 'Publish',
    unpublish: 'Unpublish',
    publishedToast: 'Listing published to the catalogue.',
    unpublishedToast: 'Listing withdrawn from the catalogue.',
    couldNotPublish: 'Could not change the publication.',
    forListing: 'Listing: {{title}}',
    fromBuyer: 'From: {{who}}',
    thisFeatureIsNotIncludedIn: 'This feature is not included in your current plan.',
    viewThePlans: 'View the plans',
    theDisplayNameIsRequired: 'The display name is required.',
    couldNotSave: 'Could not save.',
    editMyProviderProfile: 'Edit my provider profile',
    createMyProviderProfile: 'Create my provider profile',
    cancel: 'Cancel',
    saving: 'Saving…',
    save: 'Save',
    displayName: 'Display name',
    tagline: 'Tagline',
    oneSentenceThatSumsYouUp: 'One sentence that sums you up.',
    bio: 'Bio',
    localization: 'Localization',
    montrealQc: 'Montreal, QC',
    hourlyRate: 'Hourly rate',
    expertise: 'Expertise',
    separateTheTagsWithCommas: 'Separate the tags with commas.',
    marketingSeoContent: 'marketing, seo, content',
    availability: 'Availability',
    available: 'Available',
    busy: 'Busy',
    unavailable: 'Unavailable',
    theTitleIsRequired: 'The title is required.',
    title: 'Title',
    description: 'Description',
    category: 'Category',
    price: 'Price',
    deliveryTime: 'Delivery time',
    editTheService: 'Edit the service',
    newService: 'New service',
    delay: 'Delay',
    n3Days: '3 days',
    serviceActiveVisibleInTheCatalog: 'Service active (visible in the catalog)',
    accept: 'Accept',
    deny: 'Deny',
    start: 'Start',
    finish: 'Finish',
    bookingUpdated: 'Booking updated.',
    couldNotUpdate: 'Could not update.',
    quote: 'Quote',
    reservation: 'Reservation',
    receivedOnV: 'Received on {{v}}',
    paymentStatusUnavailable: 'Payment status unavailable.',
    paymentsAreNotEnabledByThe: 'Payments are not enabled by the administrator yet.',
    couldNotConnectStripe: 'Could not connect Stripe.',
    paymentsPayouts: 'Payments & payouts',
    checking: 'Checking…',
    payoutsEnabled: 'Payouts enabled',
    setupToFinish: 'Setup to finish',
    notConnected: 'Not connected',
    youCanReceivePaymentsForYour: 'You can receive payments for your bookings.',
    connectAStripeAccountToGet: 'Connect a Stripe account to get paid for your bookings.',
    redirecting: 'Redirecting…',
    managePayments: 'Manage payments',
    connectPayments: 'Connect payments',
    couldNotLoad: 'Could not load.',
    youDonTHaveAProvider: 'You don\'t have a provider profile yet. Create one to publish your services and receive bookings.',
    providerProfileCreated: 'Provider profile created.',
    noReviewsYet: 'No reviews yet',
    editTheProfile: 'Edit the profile',
    permanentlyDeleteYourProviderProfile: 'Permanently delete your provider profile?',
    providerProfileDeleted: 'Provider profile deleted.',
    couldNotDelete: 'Could not delete.',
    deleteTheProfile: 'Delete the profile',
    myServices: 'My services',
    newService2: '+ New service',
    youHaveNotPublishedAnyService: 'You have not published any service yet.',
    inactive: 'Inactive',
    edit: 'Edit',
    serviceDeleted: 'Service deleted.',
    delete: 'Delete',
    bookingsReceivedLength: 'Bookings received ({{length}})',
    noBookingReceivedYet: 'No booking received yet.',
    profileUpdated: 'Profile updated.',
  },
  providerDetailPage: {
    youCannotBookYourOwnServices: 'You cannot book your own services.',
    youHaveAlreadyReviewedThisBooking: 'You have already reviewed this booking.',
    theBookingMustBeCompletedBefore: 'The booking must be completed before it can be reviewed.',
    thisFeatureIsNotIncludedIn: 'This feature is not included in your current plan.',
    viewThePlans: 'View the plans',
    chooseADateAndTimeFor: 'Choose a date and time for the booking.',
    bookingNotPossible: 'Booking not possible.',
    bookTitle: 'Book “{{title}}”',
    contactDisplayName: 'Contact {{display_name}}',
    cancel: 'Cancel',
    sending: 'Sending…',
    sendTheRequest: 'Send the request',
    leadTimeToBeAgreed: 'Lead time to be agreed',
    requestType: 'Request type',
    firmBooking: 'Firm booking',
    quoteRequest: 'Quote request',
    preferredDateAndTime: 'Preferred date and time',
    requiredForAFirmBooking: 'Required for a firm booking.',
    optionalForAQuoteRequest: 'Optional for a quote request.',
    messageToTheProvider: 'Message to the provider',
    describeYourNeed: 'Describe your need…',
    selectACompletedBookingToReview: 'Select a completed booking to review.',
    couldNotSubmitTheReview: 'Could not submit the review.',
    reviewDisplayName: 'Review {{display_name}}',
    publishTheReview: 'Publish the review',
    bookingConcerned: 'Booking concerned',
    completedOnV: '… · completed on {{v}}',
    overallRating: 'Overall rating',
    detailedCriteria: 'Detailed criteria',
    titleOptional: 'Title (optional)',
    eGFlawlessWork: 'E.g. Flawless work',
    commentOptional: 'Comment (optional)',
    shareYourExperience: 'Share your experience…',
    providerNotFound: 'Provider not found.',
    backToExplore: 'Back to explore',
    verified: '✓ Verified',
    noReviewsYet: 'No reviews yet',
    contact: 'Contact',
    youMustHaveACompletedBooking: 'You must have a completed booking with this provider to review them.',
    review: 'Review',
    servicesOffered: 'Services offered',
    thisProviderHasNotPublishedAny: 'This provider has not published any service yet.',
    book: 'Book',
    reviewsLength: 'Reviews ({{length}})',
    noReviewsYet2: 'No reviews yet.',
    requestSentToTheProvider: 'Request sent to the provider.',
    thankYouForYourReview: 'Thank you for your review!',
  },

  catalogPage: {
    searchPlaceholder: 'Search a listing or a provider…',
    search: 'Search',
    allCategories: 'All categories',
    filterCategory: 'Filter by category',
    maxPrice: 'Max price',
    minRating: 'Minimum rating',
    anyRating: 'Any rating',
    ratingAtLeast: '{{n}} stars & up',
    sortBy: 'Sort by',
    sortRecent: 'Most recent',
    sortPriceAsc: 'Price ascending',
    sortPriceDesc: 'Price descending',
    sortRating: 'Top rated',
    resultsCount: '{{count}} listing(s)',
    couldNotLoad: 'Could not load the catalogue.',
    noListings: 'No published listing matches your search.',
    yourListing: 'Your listing',
    bySeller: 'by {{name}}',
    previous: 'Previous',
    next: 'Next',
    pageOf: 'Page {{page}} / {{pages}}',
    viewListing: 'View listing {{title}}',
    listing: 'Listing',
    listingNotFound: 'This listing is no longer available.',
    delivery: 'Delivery: {{v}}',
    seller: 'Provider',
    ownListingNotice: 'This is one of your own listings: you cannot book it.',
    bookingKind: 'Request type',
    kindRequest: 'Quote request',
    kindBooking: 'Firm booking',
    message: 'Message to the provider',
    messagePlaceholder: 'Describe your need…',
    scheduledAt: 'Preferred date (optional)',
    sendRequest: 'Send the request',
    sending: 'Sending…',
    cancel: 'Cancel',
    requestSent: 'Request sent to the provider.',
    viewMyBookings: 'View my bookings',
    couldNotSend: 'Could not send the request.',
    report: 'Report this listing',
    reportReason: 'Reason',
    reportDetails: 'Details (optional)',
    sendReport: 'Send the report',
    reportSent: 'Report sent to moderation.',
    reportAlready: 'You already reported this listing.',
    couldNotReport: 'Could not send the report.',
    reasonSpam: 'Spam',
    reasonMisleading: 'Misleading',
    reasonInappropriate: 'Inappropriate',
    reasonIllegal: 'Illegal',
    reasonOther: 'Other',
  },
  moderationPage: {
    forbidden: 'Platform superadmin only.',
    couldNotLoad: 'Could not load moderation.',
    reportsTitle: 'Open reports ({{count}})',
    noReports: 'No open report.',
    reportReason: 'Reason: {{reason}}',
    dismiss: 'Dismiss',
    listingsTitle: 'Listings of every organisation ({{count}})',
    noListings: 'No listing.',
    stateRemoved: 'Removed',
    statePublished: 'Published',
    stateDraft: 'Draft',
    openReports: '{{count}} report(s)',
    remove: 'Remove',
    restore: 'Restore',
    removeTitle: 'Remove “{{title}}” from the catalogue',
    removeReason: 'Reason for removal (sent to the seller)',
    confirmRemove: 'Confirm removal',
    cancel: 'Cancel',
    removed: 'Listing removed from the catalogue.',
    restored: 'Listing restored (draft).',
    reportClosed: 'Report closed.',
    couldNotRemove: 'Could not remove the listing.',
    couldNotRestore: 'Could not restore the listing.',
    couldNotResolve: 'Could not close the report.',
  },
};

export default en;
